import { Button, Center, Group, Loader, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPlugConnected, IconRefresh, IconTrash } from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { useNavigate, useParams } from 'react-router'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { useGetAiBridgeDetails } from '@/queries/aiBridgeQueries'
import { useGetDeviceDetails } from '@/queries/deviceQueries'
import { CrudAction, Resource } from '@/types/permissions'
import { ErrorWithReload } from '../../ui-shared/ErrorWithReload/ErrorWithReload'
import { ListItemDrawer } from '../../ui-shared/ListItemDrawer/ListItemDrawer'
import { DeleteAiBridgeModal } from '../DeleteAiBridgeModal'
import { RestartAiBridgeModal } from '../RestartAiBridgeModal'
import { useAiBridgeOperationLog } from '../useAiBridgeOperationLog'
import {
  AI_BRIDGE_TEST_RESULT_COLOR,
  useAiBridgeTest
} from '../useAiBridgeTest'
import { AiBridgeInfo } from './AiBridgeInfo'

export const AiBridgeDetails = () => {
  const navigate = useNavigate()
  const { bridgeId } = useParams()

  const isOpened = !!bridgeId

  const [canManageAiBridges] = useHasPermissions(Resource.VlmPipelines, [
    CrudAction.Create
  ])

  const [canDeleteAiBridge] = useHasPermissions(Resource.VlmPipelines, [
    CrudAction.Delete
  ])

  const [
    isDeleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal }
  ] = useDisclosure(false)

  const [
    isRestartModalOpened,
    { open: openRestartModal, close: closeRestartModal }
  ] = useDisclosure(false)

  const { testBridge, isTesting, result } = useAiBridgeTest()

  const { operationLog, recordOperation } = useAiBridgeOperationLog()

  const handleTest = async (bridgeId: string) => {
    const outcome = await testBridge(bridgeId)
    recordOperation(bridgeId, { type: 'test', ...outcome })
  }

  const {
    data: aiBridge,
    isLoading,
    isError,
    refetch
  } = useGetAiBridgeDetails(bridgeId || '', isOpened)

  // The bridge carries a device uuid only, so the name is a second lookup.
  const { data: device } = useGetDeviceDetails(
    aiBridge?.device_uuid || '',
    !!aiBridge?.device_uuid
  )

  const handleClose = () => {
    void navigate(-1)
  }

  return (
    <ListItemDrawer
      opened={isOpened}
      title={aiBridge?.name}
      onClose={handleClose}
    >
      {isLoading && (
        <Center h={120}>
          <Loader size="md" />
        </Center>
      )}

      {isError && !isLoading && (
        <ErrorWithReload onReload={() => void refetch()}>
          <FormattedMessage id="aiBridge.data.error" />
        </ErrorWithReload>
      )}

      {aiBridge && (
        <Stack maw={1100}>
          <AiBridgeInfo
            aiBridge={aiBridge}
            deviceName={device?.label}
            lastOperation={operationLog[aiBridge.id]}
          />

          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Group>
                {canManageAiBridges && (
                  <>
                    <Button
                      w={160}
                      loading={isTesting}
                      leftSection={<IconPlugConnected size={16} />}
                      onClick={() => void handleTest(aiBridge.id)}
                    >
                      <FormattedMessage id="aiBridge.test" />
                    </Button>

                    <Button
                      w={160}
                      variant="outline"
                      leftSection={<IconRefresh size={16} />}
                      onClick={openRestartModal}
                    >
                      <FormattedMessage id="aiBridge.restart" />
                    </Button>
                  </>
                )}
              </Group>

              {result && (
                <Text size="xs" c={AI_BRIDGE_TEST_RESULT_COLOR[result.status]}>
                  {result.message}
                </Text>
              )}
            </Stack>

            {canDeleteAiBridge && (
              <Button
                variant="outline"
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={openDeleteModal}
              >
                <FormattedMessage id="delete" />
              </Button>
            )}
          </Group>

          <RestartAiBridgeModal
            opened={isRestartModalOpened}
            aiBridge={aiBridge}
            onClose={closeRestartModal}
            onRestart={closeRestartModal}
          />

          <DeleteAiBridgeModal
            opened={isDeleteModalOpened}
            aiBridge={aiBridge}
            onClose={closeDeleteModal}
            onDelete={() => {
              closeDeleteModal()
              handleClose()
            }}
          />
        </Stack>
      )}
    </ListItemDrawer>
  )
}
